/**
 * Weather warnings: how a gardener wants them, and a way to see one.
 *
 * The warning itself is written and sent by the backend (services/alerts)
 * from the same to-do list Today draws, in Today's words; this only reads
 * and sets the switches, reads what sends one (AlertPreferences.sends, from
 * the server's rules), and asks for a preview or a send now from the profile.
 *
 * Endpoints (every answer also carries `success`):
 * - GET  /alerts/preferences → AlertPreferences.
 * - PUT  /alerts/preferences { byEmail, bySms } → AlertPreferences.
 * - PUT  /alerts/send-hour { hour } → AlertPreferences.
 * - POST /alerts/preview → { warning: Warning | null } (nothing is sent).
 * - POST /alerts/send-now { sampleIfQuiet: true } → SendWarningResult.
 *
 * Who uses it: user/UserProfile and user/components/WarningsCard.
 * Caching: ALERT_PREFERENCES_KEY (['alerts', 'preferences'],
 * user/components/profileQueries), invalidated after a change and after the
 * postcode changes (ProfileDetailsForm). Auth: the signed-in person's own
 * preferences.
 *
 * @module alerts
 */

import { apiClient } from './apiClient';

/** How the gardener wants weather warnings, and what they are able to have. */
export interface AlertPreferences {
  /** Warnings by email. */
  byEmail: boolean;
  /** Warnings by text; kept off while smsAvailable is false. */
  bySms: boolean;
  /** Local date of the last warning sent, or null. */
  lastSentOn: string | null;
  /** False until Brevo has an approved SMS sender; the switch stays off. */
  smsAvailable: boolean;
  /** Whether there is a phone number to text. */
  hasPhone: boolean;
  /** Whether there is a postcode to read the weather for; without one nothing is sent. */
  hasPostalCode: boolean;
  /** The hour (0–23, their own clock) the gardener chose for their warning; null for the Grow setting's. */
  sendHour: number | null;
  /** The Grow setting's hour (warnings.SEND_HOUR), for a gardener who has not chosen. */
  defaultSendHour: number;
  /** The hours a gardener may choose: 7, 12, 14 and 16. */
  hourChoices: number[];
  /** What really sends a warning, read by the server from its rules (backend services/alerts/warningSends). */
  sends: WarningSends;
}

/**
 * What sends a warning, as the server reads it from the rules the to-do list
 * reads (todo_rule), in the gardener's language: the card only draws it.
 */
export interface WarningSends {
  /** The weather a warning goes out for, most severe first: "a freeze", "frost", "cold"... Empty when none sends. */
  conditions: string[];
  /** The weather that is mentioned too when a warning goes out anyway: "a chilly night", "a downpour". */
  along: string[];
  /** What a warning's card says, as an email's subject reads ("Cover 2 plants · Tonight"); null when none sends. */
  sample: string | null;
}

/** One day's warning, in each of the shapes it is sent in (backend services/alerts). */
export interface Warning {
  /** The email's subject line: one card's title and when, or the headline. */
  subject: string;
  /** The email's lead: the first card's title and reason, or its title and how many more. Also the app notification. */
  headline: string;
  /**
   * The email's cards as lines, in Today's words ("Cover Bed A · Frost
   * tonight, 30°F · tomatoes — Bed A: Tomato"). None when only a text is due.
   */
  lines: string[];
  /** Always empty: harm rides along as a card under "Around the garden". */
  watch: string[];
  /** The email's "Around the garden" lines (the jobs riding along), in the reader's language. */
  chores: string[];
  /** Today's and tomorrow's forecast in one line. */
  forecastLine: string;
  /** The day it is for, as words. */
  dateLine: string;
  /** The garden's own day it is for, YYYY-MM-DD. */
  localDate: string;
  /** The whole warning as one text message; null when no text is due (composeWarning). */
  sms: string | null;
  /** The language it was written in: the gardener's, 'en' or 'es'. */
  language: 'en' | 'es';
}

/**
 * The gardener's warning preferences.
 *
 * @returns How they want warnings, and what is available to them.
 */
export async function getAlertPreferences(): Promise<AlertPreferences> {
  const response = await apiClient.get<AlertPreferences & { success: boolean }>('/alerts/preferences');
  return response.data;
}

/**
 * Turns warnings by email and by text on or off.
 *
 * @param byEmail - Warnings by email.
 * @param bySms - Warnings by text; the server keeps it off until texts can be sent.
 * @returns The preferences as saved.
 */
export async function setAlertPreferences(byEmail: boolean, bySms: boolean): Promise<AlertPreferences> {
  const response = await apiClient.put<AlertPreferences & { success: boolean }>('/alerts/preferences', { byEmail, bySms });
  return response.data;
}

/**
 * Sets the hour, on the gardener's own clock, their warning goes out.
 *
 * @param hour - One of the preferences' hourChoices.
 * @returns The preferences as saved.
 */
export async function setWarningHour(hour: number): Promise<AlertPreferences> {
  const response = await apiClient.put<AlertPreferences & { success: boolean }>('/alerts/send-hour', { hour });
  return response.data;
}

/**
 * Why nothing arrived, when nothing arrives.
 *
 *   QUIET          — the garden is fine and no sample was asked for.
 *   MAILER_OFF     — no mail key is configured, so nothing could be sent.
 *   MAILER_REFUSED — the mailer was asked and said no.
 */
export type SendFailure = 'QUIET' | 'MAILER_OFF' | 'MAILER_REFUSED';

/** What sending now did: sent or not, a sample or the real thing, and why not. */
export interface SendWarningResult {
  /** Whether a message went. */
  sent: boolean;
  /** True when it was a sample, on a quiet day. */
  sample: boolean;
  /** What was (or would have been) sent. */
  warning: Warning | null;
  /** Why nothing went; null when something did. */
  reason: SendFailure | null;
}

/**
 * Sends today's message now. On a quiet day, sends a sample instead, so the
 * gardener can see what one looks like before the first frost.
 *
 * A message that was composed and then refused is not a quiet day, and the
 * reason says which happened — the two used to report as the same silence,
 * which sent somebody looking at the weather over a missing key.
 *
 * @returns What was sent, or why nothing was.
 */
export async function sendWarningNow(): Promise<SendWarningResult> {
  const response = await apiClient.post<{ success: boolean } & SendWarningResult>(
    '/alerts/send-now', { sampleIfQuiet: true });
  return response.data;
}
